import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import { flowRight } from 'lodash/fp';
import ShareForm from './ShareForm';
import PendingDelete from './PendingDelete';
import Dashboard from 'ui/dashboard/Dashboard';
import DashboardBox from 'ui/dashboard/Box';
import DashboardBoxContent from 'ui/dashboard/BoxContent';
import SharesImg from '../../../images/shares-165x165.png';
import { Typography, Grid } from '@material-ui/core';

import { MTable } from 'ui/mtable/MTable';
import EditButton from 'ui/EditButton';
import DetailsButton from 'ui/DetailsButton';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';

import formatters from '../../../formatters';
import { withCompanyContext } from '../CompanyContext';

import * as api from '../../../api';

const sharesStyles = theme => ({
    button: {
        margin: theme.spacing(),
        fontSize: '10px',
    },
    leftIcon: {
        marginRight: theme.spacing(),
        fontSize: '15px',
    },
});

const DELETE_SHARES = 'deleteShares';
const EDIT_SHARE = 'editShare';

class Shares extends React.Component {
    state = {
        selectedShares: [],
        shares: [],
        sharesLoading: true,
        currentShare: null,
        currentAction: null,
        actionResultName: null,
        formErrors: null,
        loaded: false,
    };

    componentDidMount() {
        const { company } = this.props;
        this.fetchSharesForCompany(company.id);
    }

    fetchSharesForCompany(id) {
        api.get(`/api/organization/${id}/shares`).then(json => {
            this.setState({
                loaded: true,
                shares: json.data.share_batches,
                sharesLoading: false,
            });
        });
    }

    updateShare = shareData => {
        const { company } = this.props;
        shareData.organization = company.id;

        api.patch(`/api/organization/${company.id}/shares`, shareData).then(
            json => {
                if (json.errors) {
                    this.setState({
                        formErrors: json.error,
                    });
                } else {
                    this.showInfoSnackbar('Share will be updated.');
                    this.setState(
                        { sharesLoading: true, currentShare: null },
                        () => {
                            this.fetchSharesForCompany(company.id);
                        }
                    );
                }
            }
        );
    };

    deleteShares = selectedShares => {
        const { company } = this.props;
        const data = selectedShares.map(({ uuid }) => ({
            uuid,
        }));

        this.setState({ sharesLoading: true }, () => {
            this.showInfoSnackbar('Shares(s) will be deleted.');
            api.remove(`/api/organization/${company.id}/shares`, data).then(
                json => {
                    this.fetchSharesForCompany(company.id);
                }
            );
        });
    };

    fetchShare = ({ uuid }) => {
        const { company } = this.props;

        const data = {
            share_batch_uuid: uuid,
        };

        api.get(`/api/organization/${company.id}/shares`, data).then(json => {
            this.setState({
                currentAction: EDIT_SHARE,
                currentShare: json.data.share_batch_details,
            });
        });
    };

    getAvailableActions = (selectedShares, actions) => {
        return selectedShares ? actions : [];
    };

    getActions = props => {
        return [
            {
                key: DELETE_SHARES,
                name: 'Delete shares',
                action: this.handleAction(DELETE_SHARES),
            },
        ];
    };

    handleAction = currentAction => selectedShares => {
        this.setState({
            currentAction,
            selectedShares,
        });
    };

    handleCloseDialog = () => () => {
        this.setState({ currentAction: null });
    };

    handleOkDialog = ({ callback, actionResultName }) => () => {
        this.setState({ currentAction: null, actionResultName }, () =>
            callback(this.state.selectedShares, Boolean(actionResultName))
        );
    };

    handleFormCancel = () => {
        this.setState({
            formErrors: null,
            currentShare: null,
        });
    };

    handleEdit = share => () => {
        this.fetchShare(share);
    };

    renderDialog = () => {
        const { currentAction } = this.state;
        const dialog = {
            [DELETE_SHARES]: {
                title: 'Delete share',
                warning: 'Are you sure you want to delete:',
            },
        };
        const callbacks = {
            [DELETE_SHARES]: { callback: this.deleteShares },
        };

        if (currentAction === EDIT_SHARE && this.state.currentShare) {
            return (
                <ShareForm
                    open={true}
                    editable={this.props.editable}
                    currentShare={this.state.currentShare}
                    formErrors={this.state.formErrors}
                    shares={this.state.shares}
                    onCancel={() => {
                        this.setState(
                            { currentAction: null },
                            this.handleFormCancel
                        );
                    }}
                    onOk={shareData => {
                        this.updateShare(shareData);
                    }}
                    onDelete={() => {
                        this.handleAction(DELETE_SHARES)([
                            this.state.currentShare,
                        ]);
                    }}
                />
            );
        }

        if (dialog[currentAction]) {
            return (
                <ConfirmationDialog
                    open={true}
                    {...dialog[currentAction]}
                    data={this.state.selectedShares.map(item => item.nickname)}
                    onCancel={this.handleCloseDialog(currentAction)}
                    onOk={this.handleOkDialog(callbacks[currentAction])}
                />
            );
        }
    };

    formatRecipients = recipients => {
        const transformed_elements = recipients.map((recipient, index) => {
            let result = [];
            if (recipient.is_pending_delete) {
                result.push(
                    <PendingDelete key={index} name={recipient.name} />
                );
            } else {
                result.push(<span key={index}>{recipient.name}</span>);
            }
            if (index < recipients.length - 1) {
                result.push(<span key={index + 'comma'}>, </span>);
            }
            return result;
        });

        return transformed_elements;
    };

    formatRecipientsForCsv = recipients => {
        const transformed_elements = recipients.map((recipient, index) => {
            if (recipient.is_pending_delete) {
                return `${recipient.name} (pending delete)`;
            }
            return recipient.name;
        });

        return transformed_elements.join(',');
    };

    showInfoSnackbar = (msg, delay = null) => {
        let options = {
            variant: 'info',
        };
        if (delay) {
            options['autoHideDuration'] = delay;
        }
        this.props.enqueueSnackbar(msg, options);
    };

    getColumns = () => {
        return [
            {
                field: 'uuid',
                title: 'Uuid',
                hidden: true,
                notFilter: true,
                export: false,
            },
            {
                field: 'nickname',
                title: 'Name',
                render: row => <strong>{row.nickname}</strong>,
                filterType: 'select',
                cellStyle: {
                    minWidth: '15%',
                },
            },
            {
                field: 'owner',
                title: 'Owner',
                filterType: 'select',
                cellStyle: {
                    minWidth: '17%',
                },
            },
            {
                field: 'recipient_count',
                title: '# of Recipients',
                cellStyle: {
                    minWidth: '7%',
                },
            },
            {
                field: 'recipients',
                title: 'Recipients',
                render: row => {
                    const withoutOwner = row.recipients.filter(
                        item => item.permissions !== 'owner'
                    );

                    return this.formatRecipients(withoutOwner);
                },
                exportValue: val => {
                    const withoutOwner = val.filter(
                        item => item.permissions !== 'owner'
                    );

                    return this.formatRecipientsForCsv(withoutOwner);
                },
                filterType: 'select',
                filterOptions: data => {
                    data = data.map(item =>
                        item.recipients.map(rec => rec.name)
                    );
                    return [].concat(...data);
                },
                filter: (value, filter) =>
                    value.find(item => filter.value === item.name),
                cellStyle: {
                    minWidth: '43%',
                },
            },
            {
                field: 'last_modified_date',
                title: 'Last Update',
                filterType: 'select',
                render: row => formatters.date(row.last_modified_date),
                cellStyle: {
                    minWidth: '10%',
                },
            },
            {
                field: 'action',
                notFilter: true,
                title: '',
                render: row => {
                    if (this.props.editable) {
                        return <EditButton onClick={this.handleEdit(row)} />;
                    }
                    return <DetailsButton onClick={this.handleEdit(row)} />;
                },
                cellStyle: {
                    minWidth: '8%',
                },
            },
        ];
    };

    getActionMenuItem = selectedShares => {
        const actions = this.getActions(this.props);
        const availableActions = this.getAvailableActions(
            selectedShares,
            actions
        );

        const menuItems =
            availableActions.length > 0
                ? availableActions.map(action => ({
                      key: action.key,
                      name: action.name,
                      onClick: dropdownCloseCallback => {
                          action.action(selectedShares);
                          dropdownCloseCallback();
                      },
                  }))
                : [
                      {
                          key: 'no',
                          name: 'No common action',
                          onClick: dropdownCloseCallback => {
                              dropdownCloseCallback();
                          },
                      },
                  ];

        return menuItems;
    };

    handleSelectionChange = val => {
        this.setState({ selectedShares: val });
    };

    render() {
        const { shares, sharesLoading, loaded, selectedShares } = this.state;
        const { editable } = this.props;
        const columns = this.getColumns();

        const selectedActions = this.getActionMenuItem(selectedShares);
        const showTable = shares.length > 0 || (shares.length <= 0 && !loaded);
        return (
            <>
                {this.state.currentAction && this.renderDialog()}
                {showTable && (
                    <MTable
                        stateKey="Shares"
                        columns={columns}
                        data={shares}
                        loading={sharesLoading}
                        title={null}
                        actionMenuItems={selectedActions}
                        onSelectionChange={this.handleSelectionChange}
                        options={{
                            selection: editable,
                        }}
                    />
                )}
                {loaded && shares.length <= 0 && (
                    <Dashboard width="70" align="center">
                        <DashboardBox>
                            <DashboardBoxContent>
                                <Grid container spacing={3} direction="row">
                                    <Grid item xs="auto">
                                        <img src={SharesImg} alt="shares" />
                                    </Grid>
                                    <Grid item xs>
                                        <Grid
                                            container
                                            justify="center"
                                            direction="column"
                                            style={{ height: '100%' }}
                                        >
                                            <Typography
                                                variant="h5"
                                                gutterBottom
                                            >
                                                Shares created by users will
                                                appear here
                                            </Typography>
                                            <Typography
                                                color="textSecondary"
                                                variant="body1"
                                                gutterBottom
                                            >
                                                When users create shares using
                                                the CyberFOX Password Manager
                                                application the shares will
                                                appear here, where you can
                                                manage them.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </DashboardBoxContent>
                        </DashboardBox>
                    </Dashboard>
                )}
            </>
        );
    }
}

export default flowRight(
    withSnackbar,
    withCompanyContext,
    withStyles(sharesStyles)
)(Shares);
